import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { useUpdateSireMutation } from '@/app/services/donorSchedules';
import { addToast } from '@/app/slices/toast';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Sire } from '@/rtk-query.gql';
import { CurrentUser } from '@/services/auth';
import { hasPermission } from '@/services/auth/util';
import { fetchBreedById } from '@/services/lookups';
import {
  useSireQuery,
  useUpdateSireMutation as useUpdateSireRestMutation,
} from '@/services/sire';
import { verifiedOptions } from '@/types/common';

import { BreedsAutoComplete } from '../breeds-auto-complete';
import { SpeciesDropdown } from '../species-dropdown/SpeciesDropdown';
import { editSireFormSchema, EditSireFormValues } from './schema';

export type { EditSireFormValues } from './schema';

type IEditSireFormProps = {
  onCancel: () => void;
  sire?: Sire;
  onSuccess?: (data: EditSireFormValues) => void;
  onError?: (error: unknown) => void;
  calendarContainer?: HTMLElement;
};

export const EditSireForm = ({
  onCancel,
  sire,
  onSuccess,
  onError,
  calendarContainer,
}: IEditSireFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { me } = useAppSelector((s) => s.authentication);
  const [updateSireMutation] = useUpdateSireMutation();
  const isSireVerifiedEnabled = useFeatureFlag('Sire_Verified_Field');
  const { mutateAsync: updateSireRest } = useUpdateSireRestMutation();
  const { data: sireDetail } = useSireQuery(sire?.id, isSireVerifiedEnabled);
  const [selectedSubFamilies, setSelectedSubFamilies] = useState<number[]>([]);
  const [initialSubFamily, setInitialSubFamily] = useState<
    number | null | undefined
  >(undefined);
  const [breedResetKey, setBreedResetKey] = useState(0);

  const toFormValues = (s?: Sire): EditSireFormValues => ({
    sireId: s?.id || '',
    name: s?.name || '',
    breedId: s?.breed?.id || '',
    breedReg: s?.breedReg || '',
    registrationNumber: s?.registrationNumber || '',
    regPrefix: s?.regPrefix || '',
    cane: s?.cane || '',
    birth: s?.birth ? new Date(s.birth) : null,
    sire: s?.sire || '',
    heparin: s?.heparin || undefined,
    verified: undefined,
  });

  useEffect(() => {
    if (!sire?.breed?.id) {
      return;
    }
    let cancelled = false;
    fetchBreedById(sire.breed.id)
      .then((breed) => {
        if (!cancelled) {
          setInitialSubFamily(breed.subFamily ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInitialSubFamily(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sire?.breed?.id]);
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<EditSireFormValues>({
    resolver: zodResolver(editSireFormSchema),
    defaultValues: toFormValues(sire),
  });

  useEffect(() => {
    reset(toFormValues(sire));
    setBreedResetKey(0);
    setSelectedSubFamilies([]);
    setInitialSubFamily(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sire?.id, sire?.breed?.id]);

  useEffect(() => {
    if (sireDetail && sireDetail.id === sire?.id) {
      setValue('verified', sireDetail.verified ? 'Yes' : 'No');
    }
  }, [sireDetail, sire?.id, setValue]);

  const getFormErrorMessage = (name: keyof EditSireFormValues) => {
    return (
      errors[name] && (
        <small className="mt-1 text-sm text-red-500">
          {errors[name]?.message}
        </small>
      )
    );
  };

  const onSubmit = async (data: EditSireFormValues) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const { verified, ...input } = data;
      if (isSireVerifiedEnabled) {
        const { sireId, ...request } = input;
        await updateSireRest({
          sireId,
          request: {
            ...request,
            ...(verified && { verified: verified === 'Yes' }),
          },
        });
      } else {
        await updateSireMutation({ input });
      }

      await queryClient.refetchQueries({
        queryKey: ['semen'],
      });

      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: t('sire.sireUpdatedSuccessfully'),
          life: ToastLife.MEDIUM,
        }),
      );

      onSuccess?.(data);
      onCancel();
    } catch (error) {
      console.error('Failed to update sire:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('sire.failedToUpdateSire'),
          life: ToastLife.MEDIUM,
        }),
      );
      onError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const isSireEditDisabled = (me: CurrentUser | undefined) => {
    if (!me) return true;
    return !hasPermission(me, 'SEMEN_APP.EDIT_SIRE');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="label-input-group">
          <label htmlFor="name" className="required">
            {t('sire.sireName')}
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id="name"
                {...field}
                disabled={isSireEditDisabled(me)}
                tabIndex={1}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
              />
            )}
          />
          {getFormErrorMessage('name')}
        </div>

        <SpeciesDropdown
          label={t('fields.species', 'Species')}
          disabled={isSireEditDisabled(me)}
          initialSubFamily={initialSubFamily ?? null}
          tabIndex={2}
          onChange={(subFamilies) => {
            setSelectedSubFamilies(subFamilies);
          }}
          onUserChange={() => {
            setBreedResetKey((k) => k + 1);
            setValue('breedId', '');
          }}
        />

        <div className="label-input-group">
          <Controller
            name="breedId"
            control={control}
            render={({ field, fieldState }) => (
              <BreedsAutoComplete
                key={breedResetKey}
                defaultValue={
                  breedResetKey === 0 ? (sire?.breed ?? undefined) : undefined
                }
                label={`${t('fields.breed')}*`}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                subFamilies={
                  selectedSubFamilies.length > 0
                    ? selectedSubFamilies
                    : undefined
                }
                disabled={isSireEditDisabled(me)}
                tabIndex={3}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value?.id);
                }}
              />
            )}
          />
          {getFormErrorMessage('breedId')}
        </div>

        <div className="label-input-group">
          <label htmlFor="breedReg">{t('fields.breedRegistration')}</label>
          <Controller
            name="breedReg"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id="breedReg"
                {...field}
                tabIndex={4}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
              />
            )}
          />
          {getFormErrorMessage('breedReg')}
        </div>

        <div className="label-input-group">
          <label htmlFor="registrationNumber">
            {t('fields.registrationNumber')}
          </label>
          <Controller
            name="registrationNumber"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id="registrationNumber"
                {...field}
                tabIndex={5}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
              />
            )}
          />
          {getFormErrorMessage('registrationNumber')}
        </div>

        <div className="label-input-group">
          <label htmlFor="regPrefix">{t('fields.countryCode')}</label>
          <Controller
            name="regPrefix"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id="regPrefix"
                {...field}
                tabIndex={6}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
              />
            )}
          />
          {getFormErrorMessage('regPrefix')}
        </div>

        <div className="label-input-group">
          <label htmlFor="cane">{t('semen.cane')}</label>
          <Controller
            name="cane"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id="cane"
                {...field}
                tabIndex={7}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
              />
            )}
          />
          {getFormErrorMessage('cane')}
        </div>

        <div className="label-input-group">
          <label htmlFor="birth">{t('fields.birthDate')}</label>
          <Controller
            name="birth"
            control={control}
            render={({ field, fieldState }) => (
              <Calendar
                id="birth"
                value={field.value}
                onChange={(e) => field.onChange(e.value as Date)}
                dateFormat="mm/dd/yy"
                showIcon
                tabIndex={8}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                container={calendarContainer}
              />
            )}
          />
          {getFormErrorMessage('birth')}
        </div>

        <div className="label-input-group">
          <label htmlFor="sire">{t('fields.pedigreeName')}</label>
          <Controller
            name="sire"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id="sire"
                {...field}
                tabIndex={9}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
              />
            )}
          />
          {getFormErrorMessage('sire')}
        </div>

        <div className="label-input-group">
          <label htmlFor="heparin">{t('semen.heparin')}</label>
          <Controller
            name="heparin"
            control={control}
            render={({ field, fieldState }) => (
              <InputNumber
                id="heparin"
                value={field.value}
                onValueChange={(e) => field.onChange(e.value)}
                tabIndex={10}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
              />
            )}
          />
          {getFormErrorMessage('heparin')}
        </div>

        {isSireVerifiedEnabled && (
          <div className="label-input-group">
            <label htmlFor="verified">{t('sire.verified')}</label>
            <Controller
              name="verified"
              control={control}
              render={({ field }) => (
                <Dropdown
                  inputId="verified"
                  data-testid="verified"
                  value={field.value}
                  options={verifiedOptions}
                  onChange={(e) => field.onChange(e.value)}
                  tabIndex={11}
                  className="w-full"
                />
              )}
            />
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button
          type="button"
          label={t('common.cancel')}
          icon="pi pi-times"
          severity="secondary"
          onClick={handleCancel}
          disabled={isSubmitting}
        />
        <Button
          type="submit"
          label={t('common.save')}
          icon="pi pi-check"
          loading={isSubmitting}
        />
      </div>
    </form>
  );
};
